import { RefObject, lazy, LegacyRef, Ref, Suspense, useMemo, useRef, useEffect } from 'react';

import {
  rem,
  useColorModeValue,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  useOutsideClick,
  Flex,
  Spinner,
} from '@vimeo/bokeh';
import { Smile } from '@vimeo/bokeh-icons';
import type { IBaseDomElementProps } from '@vimeo/core/types/dom';
import type { Optional } from '@vimeo/core/types/generic';

import type { ISelectedEmoji } from '../../../components/emoji';
import { createDomName } from '../../../utils/dom/identifying';
import { createSvgStyle } from '../../../utils/styling/layouts';

const EmojiPanel = lazy(() => import('../../emoji').then((it) => ({ default: it.EmojiPanel })));

interface IEmojiButtonProps extends IBaseDomElementProps {
  inputRef: RefObject<HTMLInputElement | null>;
  placement: string;
  isSubmitted: boolean;
  isDisabled?: boolean;
  onEmojiSelect: (emoji: ISelectedEmoji) => void;
}

export function EmojiButton({
  id = createDomName('emoji-button'),
  className = createDomName('emoji-button'),
  inputRef,
  isSubmitted,
  placement,
  isDisabled,
  onEmojiSelect,
}: IEmojiButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRef = useRef(null as Optional<HTMLElement>);
  const contentRef = useRef(null as Optional<HTMLElement>);

  const emojiButtonActiveBackgroundColor: string = useColorModeValue('slate.100', 'grayscale.600');

  const emojiButtonBackgroundColor = useMemo(
    () => (isOpen ? `${emojiButtonActiveBackgroundColor}!important` : 'transparent'),
    [isOpen, emojiButtonActiveBackgroundColor],
  );

  useOutsideClick({
    ref: contentRef,
    handler: (event) => {
      if (
        event.target &&
        (triggerRef?.current?.contains(event.target as HTMLElement) ||
          inputRef.current?.contains(event.target as HTMLElement))
      ) {
        return;
      }

      isOpen && onClose();
    },
  });

  useEffect(() => {
    if (isSubmitted) {
      isOpen && onClose();
    }
  }, [isSubmitted, isOpen, onClose]);

  return (
    <Popover
      isLazy
      placement={placement}
      closeOnBlur={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <IconButton
          id={id}
          className={className}
          ref={triggerRef as LegacyRef<HTMLButtonElement>}
          aria-label={'emoji button'}
          type={'button'}
          size={'sm'}
          variant={'minimal'}
          icon={<Smile />}
          isDisabled={isDisabled}
          borderRadius={'sm'}
          margin={rem(4)}
          backgroundColor={emojiButtonBackgroundColor}
          _hover={{
            backgroundColor: emojiButtonActiveBackgroundColor,
          }}
          sx={createSvgStyle({ size: rem(20) })}
        />
      </PopoverTrigger>
      <PopoverContent padding={0}>
        <PopoverBody width={rem(326)} height={rem(260)}>
          <Suspense
            fallback={
              <Flex justifyContent={'center'} alignItems={'center'} width={'100%'} height={'100%'}>
                <Spinner size={'xs'} />
              </Flex>
            }
          >
            <EmojiPanel
              ref={contentRef as Ref<HTMLDivElement>}
              isOpen={isOpen}
              onSelect={onEmojiSelect}
            />
          </Suspense>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
